import { useEffect, useRef, useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ChangePasswordModal } from '../../auth/ChangePasswordModal';
import './Sidebar.css';
import { BrandTile, Wordmark } from '../BrandMark';
import { useInstanceName } from '../../InstanceName';
import { useHasCapability } from '../../Capabilities';
import type { Capability } from '../../api/capabilities';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * The extension this page needs. Absent means the framework serves it
   * and every instance has it — `Memory` and `Sessions` are core, not
   * something an instance can decline.
   */
  requires?: Capability;
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/overview',
    label: 'Overview',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.2" />
        <path d="M8 4.4v3.6l2.6 1.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    // No `requires`: `/api/activity` is served by the core (0.62.0), not
    // by an extension. The page explains an older core or a viewer role.
    to: '/agenda',
    label: 'Agenda',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="2.5" y="2.5" width="11" height="12" rx="1.3" stroke="currentColor" strokeWidth="1.2" />
        <path d="M5.5 2.5V1.6h5v.9" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M5 7.2l1.3 1.3L9 5.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="5" y1="11.2" x2="11" y2="11.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/',
    label: 'Knowledge',
    requires: 'knowledge.base',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ),
  },
  {
    to: '/team',
    label: 'Team Pulse',
    // `work.trends` because Insights is the tab it opens on. The other
    // five need `work.metrics`, `work.collect` or nothing — a page whose
    // tabs have different requirements is gated on the one it lands on,
    // and each tab reports its own absence.
    requires: 'work.trends',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M1.5 9h2.2l1.6-4.4 2.3 7.4 1.7-4.3 1.2 2.3h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/graph',
    label: 'Knowledge Graph',
    requires: 'knowledge.base',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="3" cy="3" r="2" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="13" cy="3" r="2" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="8" cy="13" r="2" stroke="currentColor" strokeWidth="1.2" />
        <line x1="4.4" y1="4.4" x2="7" y2="11.4" stroke="currentColor" strokeWidth="1.2" />
        <line x1="11.6" y1="4.4" x2="9" y2="11.4" stroke="currentColor" strokeWidth="1.2" />
        <line x1="5" y1="3" x2="11" y2="3" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ),
  },
  {
    to: '/lint',
    label: 'Health',
    requires: 'knowledge.base',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M8 1L14 4v4c0 4-3 6-6 7-3-1-6-3-6-7V4l6-3z" stroke="currentColor" strokeWidth="1.2" />
        <path d="M6 8l1.5 1.5L10.5 6.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/sessions',
    label: 'Sessions',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
        <line x1="1.5" y1="6" x2="14.5" y2="6" stroke="currentColor" strokeWidth="1.2" />
        <line x1="4" y1="9" x2="9" y2="9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="4" y1="11" x2="11" y2="11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/activity',
    label: 'Activity',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M1.5 8.5h3l2-4.5 2.5 9 2-4.5h3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/procedures',
    label: 'Procedures',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.2" />
        <path d="M8 1.4v2.2M8 12.4v2.2M1.4 8h2.2M12.4 8h2.2M3.4 3.4l1.5 1.5M11.1 11.1l1.5 1.5M12.6 3.4l-1.5 1.5M4.9 11.1l-1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/bus',
    label: 'Bus',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1.2" y="3.2" width="13.6" height="9.6" rx="1.4" stroke="currentColor" strokeWidth="1.2" />
        <path d="M1.8 4.2 8 8.6l6.2-4.4" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/findings',
    label: 'Findings',
    requires: 'reflection',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M2 3.5h12M2 8h12M2 12.5h12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="3.5" cy="3.5" r="1.2" fill="currentColor" />
        <circle cx="3.5" cy="8" r="1.2" fill="currentColor" />
        <circle cx="3.5" cy="12.5" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
  {
    to: '/pages',
    label: 'Pages',
    requires: 'knowledge.base',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="2" y="2" width="9" height="11" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="5" y="3" width="9" height="11" rx="1" stroke="currentColor" strokeWidth="1.2" fill="none" />
        <line x1="7" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        <line x1="7" y1="9" x2="12" y2="9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/sources',
    label: 'Sources',
    requires: 'knowledge.base',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path
          d="M3 2.5h7l3 3v8a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5v-11A.5.5 0 0 1 3 2.5z"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinejoin="round"
          fill="none"
        />
        <path d="M10 2.5v3h3" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none" />
        <line x1="5" y1="9" x2="11" y2="9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        <line x1="5" y1="11" x2="11" y2="11" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        <line x1="5" y1="13" x2="9" y2="13" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/memory',
    label: 'Memory',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5C5 1.5 3 3.5 3 6c0 1 .3 1.8.8 2.5C3.3 9.3 3 10.2 3 11c0 2 1.5 3.5 3.5 3.5h3C11.5 14.5 13 13 13 11c0-.8-.3-1.7-.8-2.5C12.7 7.8 13 7 13 6c0-2.5-2-4.5-5-4.5z" stroke="currentColor" strokeWidth="1.2" fill="none" />
        <line x1="6" y1="5.5" x2="10" y2="5.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        <line x1="5.5" y1="8" x2="10.5" y2="8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        <line x1="6" y1="10.5" x2="10" y2="10.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/stage',
    label: 'Stage',
    requires: 'knowledge.base',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1.5" y="2.5" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <line x1="6" y1="13.5" x2="10" y2="13.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="8" y1="11.5" x2="8" y2="13.5" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ),
  },
];

// Named for no instance: it was `orchd.sidebar.expanded` on every instance.
const SIDEBAR_STORAGE_KEY = 'machinai.sidebar.expanded';

/**
 * One nav entry, rendered only when this instance can serve it.
 *
 * Returning `null` rather than a disabled link: a greyed-out Knowledge
 * Graph on an instance that will never have one is an invitation to ask
 * why, and the answer is "this is a different deployment".
 */
function NavItemLink({
  item,
  expanded,
}: {
  item: NavItem;
  expanded: boolean;
}) {
  const available = useHasCapability(item.requires);
  if (!available) return null;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        className={({ isActive }) =>
          isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
        }
        title={expanded ? undefined : item.label}
      >
        <span className="sidebar__icon">{item.icon}</span>
        <span className="sidebar__label">{item.label}</span>
      </NavLink>
    </li>
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const instanceName = useInstanceName();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, expanded ? '1' : '0');
    } catch {
      /* localStorage unavailable (private mode) — keep in-memory only */
    }
  }, [expanded]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const openChangePassword = () => {
    setMenuOpen(false);
    setShowChangePassword(true);
  };

  return (
    <>
      <nav
        className={`sidebar${expanded ? ' sidebar--expanded' : ''}`}
        aria-label="Primary"
      >
        <div className="sidebar__brand" aria-label={instanceName}>
          <BrandTile className="sidebar__logo" tile={32} glyph={26} />
          <Wordmark className="sidebar__wordmark" name={instanceName} />
        </div>
        <div className="sidebar__divider" />
        <ul className="sidebar__nav">
          {NAV_ITEMS.map((item) => (
            <NavItemLink key={item.to} item={item} expanded={expanded} />
          ))}
        </ul>
        <div className="sidebar__spacer" />
        {user && (
          <div className="sidebar__user-wrap" ref={menuRef}>
            {menuOpen && (
              <div className="sidebar__menu glass" role="menu">
                <div className="sidebar__menu-head">
                  <div className="sidebar__menu-user">{user.username}</div>
                  <div className="sidebar__menu-role">{user.role}</div>
                </div>
                <button
                  type="button"
                  className="sidebar__menu-item"
                  onClick={openChangePassword}
                >
                  Change password
                </button>
                <button
                  type="button"
                  className="sidebar__menu-item sidebar__menu-item--danger"
                  onClick={handleLogout}
                >
                  Sign out
                </button>
              </div>
            )}
            <button
              type="button"
              className={`sidebar__user${menuOpen ? ' sidebar__user--open' : ''}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title={user.username}
            >
              <span className="sidebar__avatar">
                {user.username.charAt(0).toUpperCase()}
              </span>
              <span className="sidebar__user-name">{user.username}</span>
            </button>
          </div>
        )}
        <button
          type="button"
          className="sidebar__toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-pressed={expanded}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <span className="sidebar__icon sidebar__toggle-chevron">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M6 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="sidebar__label">Collapse</span>
        </button>
      </nav>
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </>
  );
}
